"use client";

import { useContactFormSubmission } from "@/app/hooks/main-hooks";
import { motion } from "framer-motion";
import { Mail, Phone, Send, User } from "lucide-react";
import { useLocale } from "next-intl";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { useForm } from "react-hook-form";
import ContactHead from "../contact/ContactHead";
import ContactInfo from "../contact/ContactInfo";

interface ContactFormData {
  name: string;
  email: string;
  phone: string;
  message: string;
}

export default function ContactSection() {
  const locale = useLocale();
  const router = useRouter();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ContactFormData>();

  const { mutate, isPending } = useContactFormSubmission();

  const onSubmit = async (data: ContactFormData) => {
    mutate(data, {
      onSuccess: () => {
        reset();
        router.push(`/${locale}/thank-you`);
      },
    });
  };

  return (
    <section
      id="contact"
      className="relative py-36 overflow-hidden bg-linear-to-b from-background via-background/80 to-background"
    >
      {/* Decorative blur shapes */}
      <div className="absolute -top-32 -left-32 w-96 h-96 bg-[#c9a24d]/20 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-0 w-md h-112 bg-primary/10 rounded-full blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-24 items-center">
        {/* Left Content */}
        <motion.div
          initial={{ opacity: 0, x: -60 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.9, ease: "easeOut" }}
          className="space-y-12"
        >
          <ContactHead locale={locale} />

          <ContactInfo />

          {/* Image */}
          <div className="relative group rounded-3xl overflow-hidden border border-border shadow-xl">
            <Image
              src="/contact.jpeg"
              alt="ERP Consulting"
              width={900}
              height={600}
              className="object-cover transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-linear-to-tr from-black/60 via-black/30 to-transparent" />
            <div className="absolute bottom-6 left-6 right-6">
              <h4 className="text-white text-xl font-semibold mb-1">
                {locale === "en"
                  ? "Let’s build something great"
                  : "لنصنع شيئًا عظيمًا"}
              </h4>
              <p className="text-white/80 text-sm">
                {locale === "en"
                  ? "Enterprise-grade solutions tailored to your business."
                  : "حلول مؤسسية مصممة خصيصًا لعملك."}
              </p>
            </div>
          </div>
        </motion.div>

        {/* Form Card */}
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: "easeOut" }}
          className="relative bg-card/60 backdrop-blur-2xl border border-border/60 rounded-3xl p-12 shadow-2xl"
        >
          {/* Gradient border glow */}
          <div className="absolute inset-0 rounded-3xl pointer-events-none bg-linear-to-br from-[#c9a24d]/30 via-transparent to-primary/20 opacity-50" />

          <h3 className="text-3xl font-bold mb-10 tracking-tight">
            {locale === "en" ? "Get in Touch" : "تواصل معنا"}
          </h3>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-7">
            <div className="group">
              <label className="text-sm font-medium mb-2 flex items-center gap-2 text-muted-foreground group-focus-within:text-[#c9a24d] transition">
                <User size={16} />{" "}
                {locale === "en" ? "Full Name" : "الاسم الكامل"}
              </label>
              <input
                {...register("name", { required: "Name is required" })}
                placeholder={locale === "en" ? "John Doe" : "محمد أحمد"}
                className="w-full px-4 py-3.5 rounded-xl bg-input/50 border border-border focus:border-[#c9a24d] focus:ring-4 focus:ring-[#c9a24d]/20 outline-none transition shadow-sm"
              />
              {errors.name && (
                <p className="text-destructive text-sm mt-1.5">
                  {errors.name.message}
                </p>
              )}
            </div>

            {/* Email */}
            <div className="group">
              <label className="text-sm font-medium mb-2 flex items-center gap-2 text-muted-foreground group-focus-within:text-[#c9a24d] transition">
                <Mail size={16} />{" "}
                {locale === "en" ? "Email Address" : "البريد الإلكتروني"}
              </label>
              <input
                {...register("email", {
                  required: "Email is required",
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: "Invalid email address",
                  },
                })}
                placeholder="you@company.com"
                className="w-full px-4 py-3.5 rounded-xl bg-input/50 border border-border focus:border-[#c9a24d] focus:ring-4 focus:ring-[#c9a24d]/20 outline-none transition shadow-sm"
              />
              {errors.email && (
                <p className="text-destructive text-sm mt-1.5">
                  {errors.email.message}
                </p>
              )}
            </div>

            {/* Phone */}
            <div className="group">
              <label className="text-sm font-medium mb-2 flex items-center gap-2 text-muted-foreground group-focus-within:text-[#c9a24d] transition">
                <Phone size={16} />{" "}
                {locale === "en" ? "Phone Number" : "رقم الهاتف"}
              </label>
              <input
                {...register("phone", {
                  required: "Phone number is required",
                  pattern: {
                    value: /^[0-9+ ]{7,20}$/,
                    message: "Invalid phone number",
                  },
                })}
                placeholder={
                  locale === "en"
                    ? "+20 100 123 4567"
                    : "مثال: +20 100 123 4567"
                }
                className="w-full px-4 py-3.5 rounded-xl bg-input/50 border border-border focus:border-[#c9a24d] focus:ring-4 focus:ring-[#c9a24d]/20 outline-none transition shadow-sm"
              />
              {errors.phone && (
                <p className="text-destructive text-sm mt-1.5">
                  {errors.phone.message}
                </p>
              )}
            </div>

            {/* Message */}
            <div className="group">
              <label className="text-sm font-medium mb-2 text-muted-foreground group-focus-within:text-[#c9a24d] transition">
                {locale === "en" ? "Your Message" : "رسالتك"}
              </label>
              <textarea
                rows={5}
                {...register("message", { required: "Message is required" })}
                placeholder={
                  locale === "en"
                    ? "Tell us about your business needs..."
                    : "أخبرنا عن احتياجات عملك..."
                }
                className="w-full px-4 py-3.5 rounded-xl bg-input/50 border border-border focus:border-[#c9a24d] focus:ring-4 focus:ring-[#c9a24d]/20 outline-none transition shadow-sm resize-none"
              />
              {errors.message && (
                <p className="text-destructive text-sm mt-1.5">
                  {errors.message.message}
                </p>
              )}
            </div>

            {/* Submit */}
            <motion.button
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.96 }}
              disabled={isPending}
              type="submit"
              className="w-full relative overflow-hidden rounded-xl bg-linear-to-r from-[#c9a24d] via-[#d6b15f] to-[#c9a24d] px-6 py-4 font-semibold text-primary-foreground disabled:opacity-60 disabled:cursor-not-allowed shadow-lg"
            >
              <span className="relative z-10 flex items-center justify-center gap-2 text-base">
                {isPending
                  ? locale === "en"
                    ? "Sending..."
                    : "جارٍ الإرسال..."
                  : locale === "en"
                    ? "Send Message"
                    : "إرسال الرسالة"}
                <Send size={18} />
              </span>
              <div className="absolute inset-0 opacity-0 hover:opacity-100 transition bg-linear-to-r from-accent to-primary" />
            </motion.button>
          </form>
        </motion.div>
      </div>
    </section>
  );
}
